import api from "./api";

/** Fired on window when any request answers 402 with a gate code (see api.js). */
export const TRIAL_EXPIRED_EVENT = "business:trial-expired";

/** The 402 codes: the business may only use billing (and profile) until it has a usable plan. */
export const GATE_CODES = ["trial_expired", "payment_pending", "plan_required"];

/** Routes still open while gated; everything else goes to /billing. */
export const TRIAL_EXPIRED_OPEN_PATHS = ["/billing", "/business", "/security", "/notification-target"];

/** The customer site: a custom plan's relative contact link lives there. */
const CUSTOMER_PORTAL_URL = (process.env.REACT_APP_CUSTOMER_PORTAL_URL || "").replace(/\/+$/, "");

/** The signed-in business's subscription block from /business/me (or null). */
export const subscriptionOf = (user) => user?.business?.subscription ?? null;

/**
 * Whether the portal is limited to billing: a trial past its end, a paid plan
 * waiting for payment with nothing to fall back on, or no usable plan. The
 * server decides (`gated`, read with the clock); `status` is the fallback for
 * an older cached profile.
 */
export const isGated = (user) => {
  const sub = subscriptionOf(user);
  if (!sub) return false;
  return typeof sub.gated === "boolean" ? sub.gated : sub.status === "trial_expired";
};

/** GET /business/subscription — status, trial, pending request, price snapshot. */
export const fetchSubscription = async () => {
  const { data } = await api.get("/business/subscription");
  return data.data;
};

/**
 * POST /business/subscription — change plan (owner only). A paid plan takes a
 * cycle and optionally a promo code and becomes a request waiting for payment
 * confirmation; free plans apply at once; custom plans are refused.
 */
export const changePlan = async ({ planId, cycleId, promoCode }) => {
  const { data } = await api.post("/business/subscription", {
    plan_id: planId,
    ...(cycleId ? { billing_cycle_id: cycleId } : {}),
    ...(promoCode ? { promo_code: promoCode } : {}),
  });
  return data;
};

/** DELETE /business/subscription/pending — withdraw the request waiting for payment (owner only). */
export const cancelPendingPlan = async () => {
  const { data } = await api.delete("/business/subscription/pending");
  return data;
};

/**
 * POST /business/promo-codes/validate — the paid plan's quote with the code
 * applied, priced in the business's currency; 422 on `promo_code` otherwise.
 */
export const validateBusinessPromo = async ({ code, planId, cycleId }) => {
  const { data } = await api.post("/business/promo-codes/validate", {
    code,
    plan_id: planId,
    ...(cycleId ? { billing_cycle_id: cycleId } : {}),
  });
  return data.data.quote;
};

/**
 * Where a custom plan's "Contact us" button goes: an absolute link as is, a
 * path on the customer site (default /pages/contact) against its URL. Null
 * when it is a path and the customer site URL is not configured.
 */
export const ctaHref = (cta) => {
  if (!cta?.url) return null;
  if (cta.is_external || /^https?:\/\//i.test(cta.url)) return cta.url;
  return CUSTOMER_PORTAL_URL ? `${CUSTOMER_PORTAL_URL}${cta.url}` : null;
};
